import { useCallback } from 'react';

import getRecaptchaSiteKey from '$utilities/recaptcha-key';

/* ========================================================================= */
/* useRecaptcha */
/* ========================================================================= */

/**
 * Loads the reCAPTCHA v3 script on first use and returns a function
 * that resolves a token for a given action name.
 *
 * @returns {{ getToken: (action?: string) => Promise<string | null> }}
 */
export const useRecaptcha = () => {
    const getToken = useCallback(async (action = 'submit') => {
        const siteKey = getRecaptchaSiteKey();

        if (!siteKey) {
            return null;
        }

        // Load script if not already present
        if (!document.querySelector(`script[data-recaptcha="${siteKey}"]`)) {
            await new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
                script.dataset.recaptcha = siteKey;
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            });
        }

        return new Promise((resolve) => {
            window.grecaptcha.ready(async () => {
                try {
                    const token = await window.grecaptcha.execute(siteKey, { action });
                    resolve(token);
                } catch {
                    resolve(null);
                }
            });
        });
    }, []);

    return { getToken };
};
